// src/context/FileContext.jsx
import React, { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef } from "react";
import { metenApi } from "../services/metenApi";
import { useEngine } from "./EngineContext";
import { useHistory } from "./HistoryContext";
import { DEFAULT_CONFIG } from "../components/config/defaultConfig";
import { withNewProjectDefaults } from "../components/config/newProjectDefaults";
import { normalizeProjectPvLosses, prepareProjectConfigForPersistence } from "../components/config/pvLosses";
import { useAuth } from "./AuthContext";
import { useOptionalDemo } from "./DemoContext";
import { useIsGreek } from "./LanguageContext";
import { pendingSectionSaveMessage } from "./pendingSectionSaveMessage";
import { validateProjectExportPayload } from "../utils/exportProject";
import {
  splitPortfolio,
  composeForPersistence,
  materialiseAssets,
  freshPortfolio,
  newAssetConfig,
  newAssetId,
  ASSET_STATUSES,
  reconcileSavedPortfolio,
} from "../utils/portfolio";

import { applyAssetType, deriveProjectAssets } from "../utils/projectAssets";

const FileContext = createContext(null);

const TRANSFORMER_EVENT = "transformerValidationFailed";
const LAST_PROJECT_KEY = "meten_last_project_id";

function validateTransformerConfig(config) {
  const orientationDisabled =
    config?.mountType === "single_axis" || config?.mountType === "dual_axis";
  const orientation = config?.panelOrientation || {};
  const tilt = orientation.tilt;
  const azim = orientation.azim;

  const isInvalidNumber = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value));

  const hasMissingOrientation =
    deriveProjectAssets(config).hasPv && !orientationDisabled && (isInvalidNumber(tilt) || isInvalidNumber(azim));

  if (!config?.hasTransformer) {
    return { hasMissing: hasMissingOrientation };
  }

  const details = config.transformerDets || {};
  const loadLoss = details.loadLoss ?? details.load;
  const noLoadLoss = details.noLoadLoss ?? details.noLoad;
  const rated = details.rated ?? details.ratedPower;
  const isInvalidPositiveNumber = (value) =>
    isInvalidNumber(value) || Number(value) <= 0;

  const hasMissing =
    hasMissingOrientation ||
    isInvalidPositiveNumber(loadLoss) ||
    isInvalidPositiveNumber(noLoadLoss) ||
    (!config.useRecommendedTransformer && isInvalidPositiveNumber(rated));

  return { hasMissing };
}

function normalizeTransformerDets(cfg) {
  const details = cfg.transformerDets || {};

  const normalizedDetails = {
    // Spread FIRST so the POI-chain fields survive: topology, chainLossMode,
    // powerFactor, energisedHoursYear, chainSegments, negativePriceHandling,
    // includeCableLosses, cable, battery, wind, hv and the include flags.
    //
    // Without it this rebuilt only the three legacy keys and dropped every
    // other one. It runs on the PROJECT LOAD path (applyProject), so a saved
    // chain configuration was discarded the moment the project was reopened:
    // the settings were stored correctly and then thrown away on the way back
    // in, and the panel fell back to chainLossMode "off" — "Not modelled".
    //
    // The identical function in components/ConfigForm.jsx was fixed for exactly
    // this and carries the same note; this second copy was missed. Two
    // normalizers for one shape is the underlying defect — if either grows a
    // field, change both.
    ...details,
    rated: details.rated ?? details.ratedPower ?? "",
    noLoadLoss: details.noLoadLoss ?? details.noLoad ?? "",
    loadLoss: details.loadLoss ?? details.load ?? "",
  };

  return { ...cfg, transformerDets: normalizedDetails };
}

function normalizeProjectConfig(cfg) {
  return normalizeProjectPvLosses(normalizeTransformerDets(cfg));
}

/** What "saved" looks like for the portfolio: every asset's config and metadata, plus the project fields. */
function savedSnapshot(portfolio, present) {
  return {
    project: portfolio?.project || null,
    assets: materialiseAssets(portfolio, present).map(({ id, name, status, requiresReview, notes, config }) => ({ id, name, status, requiresReview, notes, config })),
  };
}

function emitTransformerValidationError() {
  try {
    window.dispatchEvent(new Event(TRANSFORMER_EVENT));
  } catch {
    // window might not exist (e.g., SSR)
  }
}

export function FileProvider({ children }) {
  const { setConfig } = useEngine();
  const isGreek = useIsGreek();
  const history = useHistory();
  const historyInit = history.init;
  const { user, isSessionReady } = useAuth();
  // Saving a new project changes the project count shown in the top bar.
  const refreshUsage = useOptionalDemo()?.refreshDemoProfile;

  const [currentProject, setCurrentProject] = useState(null); // {id,name,config,...}
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // The project → assets layer (see utils/portfolio.js). `history.present` is
  // always the ACTIVE asset's config, so every existing page keeps working on
  // one asset; the other assets' configs wait here until they are switched in.
  const [portfolio, setPortfolio] = useState(() => freshPortfolio("Asset 1"));
  const [portfolioSaved, setPortfolioSaved] = useState(null); // materialised assets at last load/save
  const portfolioRef = useRef(portfolio);
  portfolioRef.current = portfolio;
  const presentRef = useRef(null);
  presentRef.current = history.present;
  const projectGenerationRef = useRef(0);
  const saveInFlightRef = useRef(false);
  const lastUserIdRef = useRef(user?.id ?? null);
  const lastProjectKey = useMemo(
    () => (user?.id ? `${LAST_PROJECT_KEY}:${user.id}` : null),
    [user?.id]
  );

  const persistLastProjectId = useCallback((project) => {
    if (typeof window === "undefined") return;
    try {
      if (!lastProjectKey) return;
      if (project?.id) localStorage.setItem(lastProjectKey, String(project.id));
      else localStorage.removeItem(lastProjectKey);
    } catch {}
  }, [lastProjectKey]);

  const applyProject = useCallback(
    (project) => {
      if (!project) return;

      const stored = project.config || {};
      const { root, portfolio: p } = splitPortfolio(stored, project?.name);
      const normalizedCfg = normalizeProjectConfig(root);
      projectGenerationRef.current += 1;
      setCurrentProject(project);
      persistLastProjectId(project);
      historyInit(normalizedCfg);
      setPortfolio(p);
      setPortfolioSaved(savedSnapshot(p, normalizedCfg));
      setIsDirty(false);
    },
    [historyInit, persistLastProjectId]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isSessionReady || !user?.id || !lastProjectKey) return;

    let cancelled = false;

    const restoreLastProject = async () => {
      let lastProjectId;
      try {
        lastProjectId = localStorage.getItem(lastProjectKey);
      } catch {
        return;
      }

      if (!lastProjectId) return;

      try {
        const project = await metenApi.getProject(lastProjectId);
        if (cancelled || !project) return;
        applyProject(project);
      } catch {
        // ignore restore failure
      }
    };

    restoreLastProject();

    return () => {
      cancelled = true;
    };
  }, [applyProject, isSessionReady, lastProjectKey, user?.id]);

  useEffect(() => {
    if (!isSessionReady) return;
    const prevUserId = lastUserIdRef.current;
    const nextUserId = user?.id ?? null;
    if (prevUserId === nextUserId) return;
    lastUserIdRef.current = nextUserId;

    projectGenerationRef.current += 1;
    setCurrentProject(null);
    setIsDirty(false);
    history.reset ? history.reset(DEFAULT_CONFIG) : history.init(DEFAULT_CONFIG);
    setPortfolio(freshPortfolio("Asset 1"));
    setPortfolioSaved(null);

    try {
      if (prevUserId) localStorage.removeItem(`${LAST_PROJECT_KEY}:${prevUserId}`);
      localStorage.removeItem("pvConfig_full");
      localStorage.removeItem("meten_last_status");
      localStorage.removeItem("meten_last_run");
      localStorage.removeItem("meten_sim_range");
    } catch {}
  }, [history, isSessionReady, user?.id]);

  // Single sync: History -> EngineContext + localStorage listeners
  useEffect(() => {
    if (history.present == null) return;

    setConfig(history.present);

    try {
      localStorage.setItem("pvConfig_full", JSON.stringify(history.present));
    } catch {}
    window.dispatchEvent(new Event("pvConfigChanged"));
  }, [history.present, setConfig]);

  // Dirty is only meaningful when a project is open. The portfolio is dirty
  // when any asset's materialised config or metadata differs from the last
  // load/save, which also covers edits made on an asset that was switched away.
  const portfolioDirty = useMemo(() => {
    if (!portfolioSaved) return false;
    const cur = savedSnapshot(portfolio, history.present);
    return JSON.stringify(cur) !== JSON.stringify(portfolioSaved);
  }, [portfolio, portfolioSaved, history.present]);

  useEffect(() => {
    setIsDirty(!!currentProject && (history.isDirty || portfolioDirty || !currentProject.id));
  }, [currentProject, history.isDirty, portfolioDirty]);

  // initialConfig: the starting settings chosen in "What study do you want?"
  // (config/studyPresets.js). Without one, a new project starts from
  // DEFAULT_CONFIG exactly as before.
  const newFile = useCallback((initialConfig) => {
    const startConfig = initialConfig && typeof initialConfig === "object" && "systemType" in initialConfig
      ? initialConfig : withNewProjectDefaults(DEFAULT_CONFIG);
    projectGenerationRef.current += 1;
    setCurrentProject(null);
    persistLastProjectId(null);
    history.reset ? history.reset(startConfig) : history.init(startConfig);
    const p = freshPortfolio("Asset 1");
    setPortfolio(p);
    setPortfolioSaved(null);
    setIsDirty(false);

    localStorage.removeItem("meten_last_status");
    localStorage.removeItem("meten_last_run");
    localStorage.removeItem("meten_sim_range");
  }, [history, persistLastProjectId]);

  const open = useCallback(async (projectId) => {
    const generation = ++projectGenerationRef.current;
    const proj = await metenApi.getProject(projectId);
    if (generation !== projectGenerationRef.current) return;
    applyProject(proj);
  }, [applyProject]);

  const importProjectJson = useCallback((payload) => {
    const imported = validateProjectExportPayload(payload);
    const { root, portfolio: p } = splitPortfolio(imported.config, imported.projectName);
    const normalized = normalizeProjectConfig(root);
    const importedProject = {
      id: null,
      name: imported.projectName || normalized.projectName || "Imported Project",
      config: normalized,
      importedSourceProjectId: imported.sourceProjectId,
      importedExportedAt: imported.exportedAt,
    };

    projectGenerationRef.current += 1;
    setCurrentProject(importedProject);
    persistLastProjectId(null);
    historyInit(normalized);
    setPortfolio(p);
    setPortfolioSaved(null);
    setIsDirty(true);

    return importedProject;
  }, [historyInit, persistLastProjectId]);

  /**
   * Sections with their own Save button that currently hold unsaved edits.
   *
   * The electrical design and the physical layout are edited in tabs that save
   * into the config through their own button; until that is pressed, the typed
   * values exist only in the tab. Saving the PROJECT at that moment would write
   * the old values and look like it had saved the new ones. So a tab registers
   * itself here while it is dirty, and the project save refuses with a sentence
   * that names the tab to save first.
   */
  const [pendingSections, setPendingSections] = useState({});
  const setSectionDirty = useCallback((name, dirty) => {
    setPendingSections((prev) => {
      if (Boolean(prev[name]) === Boolean(dirty)) return prev;
      const next = { ...prev };
      if (dirty) next[name] = true; else delete next[name];
      return next;
    });
  }, []);
  const pendingSectionNames = useMemo(() => Object.keys(pendingSections), [pendingSections]);

  const acceptSavedProject = useCallback((project, submitted, generation) => {
    // A save may finish after the user opens another project or starts a new one.
    if (generation !== projectGenerationRef.current) return;
    const saved = splitPortfolio(project.config || {}, project.name);
    saved.root = normalizeProjectConfig(saved.root);
    const merged = reconcileSavedPortfolio(submitted, {
      root: presentRef.current ?? DEFAULT_CONFIG,
      portfolio: portfolioRef.current,
    }, saved);
    setCurrentProject(project);
    persistLastProjectId(project);
    portfolioRef.current = merged.portfolio;
    presentRef.current = merged.root;
    const savedActive = materialiseAssets(saved.portfolio, saved.root)
      .find((a) => a.id === merged.portfolio.activeAssetId);
    historyInit(merged.root, savedActive ? normalizeProjectConfig(savedActive.config) : null);
    setPortfolio(merged.portfolio);
    // The server response is the saved baseline. Any edits retained above stay dirty.
    const baseline = savedSnapshot(saved.portfolio, saved.root);
    setPortfolioSaved(baseline);
    setIsDirty(JSON.stringify(savedSnapshot(merged.portfolio, merged.root)) !== JSON.stringify(baseline));
  }, [historyInit, persistLastProjectId]);

  const save = useCallback(async () => {
    if (saveInFlightRef.current) throw new Error(isGreek ? "Περιμένετε να ολοκληρωθεί η τρέχουσα αποθήκευση του έργου." : "Wait for the current project save to finish.");
    if (!currentProject?.id) {
      throw new Error(isGreek ? "Δεν υπάρχει αποθηκευμένο έργο. Χρησιμοποιήστε πρώτα «Αποθήκευση ως»." : "Nothing to save. Use Save As first.");
    }
    if (pendingSectionNames.length) {
      throw new Error(pendingSectionSaveMessage(pendingSectionNames, isGreek));
    }

    const snapshot = history.present ?? DEFAULT_CONFIG;
    const normalized = normalizeProjectConfig(snapshot);
    const payloadConfig = prepareProjectConfigForPersistence(normalized);

    const { hasMissing } = validateTransformerConfig(normalized);
    if (hasMissing) {
      emitTransformerValidationError();
      throw new Error(isGreek ? "Λείπουν υποχρεωτικές παράμετροι." : "Required parameters are missing");
    }

    const generation = projectGenerationRef.current;
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      const updated = await metenApi.updateProject(currentProject.id, {
        config: composeForPersistence(payloadConfig, portfolio),
        expected_updated_at: currentProject.updated_at,
      });
      acceptSavedProject(updated, { root: snapshot, portfolio }, generation);
      return updated;
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentProject, history, acceptSavedProject, pendingSectionNames, portfolio, isGreek]);

  const saveAs = useCallback(async ({ name }) => {
    if (saveInFlightRef.current) throw new Error(isGreek ? "Περιμένετε να ολοκληρωθεί η τρέχουσα αποθήκευση του έργου." : "Wait for the current project save to finish.");
    const snapshot = history.present ?? DEFAULT_CONFIG;
    const normalized = normalizeProjectConfig(snapshot);
    const payloadConfig = prepareProjectConfigForPersistence(normalized);

    const { hasMissing } = validateTransformerConfig(normalized);
    if (hasMissing) {
      emitTransformerValidationError();
      throw new Error(isGreek ? "Λείπουν υποχρεωτικές παράμετροι." : "Required parameters are missing");
    }

    const generation = projectGenerationRef.current;
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      const created = await metenApi.createProject({
        name,
        config: composeForPersistence(payloadConfig, portfolio),
      });
      acceptSavedProject(created, { root: snapshot, portfolio }, generation);
      if (refreshUsage) void refreshUsage({ quiet: true });
      return created;
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [history, acceptSavedProject, portfolio, isGreek, refreshUsage]);

  // A new project, saved at once under `name` («Τι μελέτη θέλετε;»). The
  // request goes first and the screen switches only when it succeeds: a
  // refused save (a name already in use, the plan's project limit) leaves the
  // open project exactly as it was, so the dialog can say why and stay open.
  const createNewProject = useCallback(async ({ name, config }) => {
    if (saveInFlightRef.current) throw new Error(isGreek ? "Περιμένετε να ολοκληρωθεί η τρέχουσα αποθήκευση του έργου." : "Wait for the current project save to finish.");
    const startConfig = config && typeof config === "object" && "systemType" in config ? config : DEFAULT_CONFIG;
    const normalized = normalizeProjectConfig(startConfig);
    const payloadConfig = prepareProjectConfigForPersistence(normalized);
    const { hasMissing } = validateTransformerConfig(normalized);
    if (hasMissing) {
      emitTransformerValidationError();
      throw new Error(isGreek ? "Λείπουν υποχρεωτικές παράμετροι." : "Required parameters are missing");
    }
    const p = freshPortfolio("Asset 1");
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      const created = await metenApi.createProject({
        name,
        config: composeForPersistence(payloadConfig, p),
      });
      projectGenerationRef.current += 1;
      const generation = projectGenerationRef.current;
      // The new project replaces the open one: what acceptSavedProject
      // reconciles against is the project just sent, not the one on screen.
      presentRef.current = startConfig;
      portfolioRef.current = p;
      acceptSavedProject(created, { root: startConfig, portfolio: p }, generation);
      localStorage.removeItem("meten_last_status");
      localStorage.removeItem("meten_last_run");
      localStorage.removeItem("meten_sim_range");
      if (refreshUsage) void refreshUsage({ quiet: true });
      return created;
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [acceptSavedProject, isGreek, refreshUsage]);

  const rename = useCallback(async (newName) => {
    if (saveInFlightRef.current) throw new Error(isGreek ? "Περιμένετε να ολοκληρωθεί η τρέχουσα αποθήκευση του έργου." : "Wait for the current project save to finish.");
    if (!currentProject?.id) throw new Error(isGreek ? "Δεν έχει ανοίξει έργο." : "No project open.");
    const generation = projectGenerationRef.current;
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      const updated = await metenApi.updateProject(currentProject.id, {
        name: newName, expected_updated_at: currentProject.updated_at,
      });
      if (generation === projectGenerationRef.current) {
        setCurrentProject(updated);
        persistLastProjectId(updated);
      }
      return updated;
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [currentProject, persistLastProjectId, isGreek]);

  // ---- assets -------------------------------------------------------------
  const assets = useMemo(() => materialiseAssets(portfolio, history.present), [portfolio, history.present]);
  const activeAsset = useMemo(
    () => assets.find((a) => a.id === portfolio.activeAssetId) || assets[0] || null,
    [assets, portfolio.activeAssetId]
  );

  /** Make another asset the one every page edits. Parks the current one first. */
  const switchAsset = useCallback(
    (assetId) => {
      const p = portfolioRef.current;
      if (!assetId || assetId === p.activeAssetId) return;
      const target = p.assets.find((a) => a.id === assetId);
      if (!target) return;
      const present = presentRef.current ?? DEFAULT_CONFIG;
      const nextCfg = normalizeProjectConfig(target.config || newAssetConfig(p.project?.description));
      const next = {
        ...p,
        activeAssetId: assetId,
        assets: p.assets.map((a) => {
          if (a.id === p.activeAssetId) return { ...a, config: present };
          if (a.id === assetId) return { ...a, config: null };
          return a;
        }),
      };
      // The undo stack is per asset session; the project-level dirty flag
      // (portfolioDirty) keeps track of the parked asset's edits.
      historyInit(nextCfg);
      setPortfolio(next);
    },
    [historyInit]
  );

  const addAsset = useCallback(({ name, systemType, fromAssetId, status = "study", activate = false } = {}) => {
    const p = portfolioRef.current;
    const id = newAssetId();
    const assetName = (name || "").trim() || `Asset ${p.assets.length + 1}`;
    let config;
    if (fromAssetId) {
      const src = p.assets.find((a) => a.id === fromAssetId);
      const srcCfg = src ? (src.id === p.activeAssetId ? presentRef.current : src.config) : null;
      config = srcCfg ? JSON.parse(JSON.stringify(srcCfg)) : newAssetConfig(p.project?.description);
    } else {
      config = newAssetConfig(p.project?.description);
    }
    config = applyAssetType(config, systemType);
    const entry = { id, name: assetName, status: ASSET_STATUSES.includes(status) ? status : "study", notes: "", config };
    if (!activate) {
      setPortfolio({ ...p, assets: [...p.assets, entry] });
      return id;
    }
    // Create and make it the edited asset in one step: park the current one.
    const present = presentRef.current ?? DEFAULT_CONFIG;
    const next = {
      ...p,
      activeAssetId: id,
      assets: [...p.assets.map((a) => (a.id === p.activeAssetId ? { ...a, config: present } : a)), { ...entry, config: null }],
    };
    historyInit(normalizeProjectConfig(config));
    setPortfolio(next);
    return id;
  }, [historyInit]);

  const updateAsset = useCallback((assetId, patch) => {
    setPortfolio((p) => ({
      ...p,
      assets: p.assets.map((a) => {
        if (a.id !== assetId) return a;
        const next = { ...a, ...patch };
        if (patch.status && !ASSET_STATUSES.includes(patch.status)) next.status = a.status;
        return next;
      }),
    }));
  }, []);

  const removeAsset = useCallback((assetId) => {
    const p = portfolioRef.current;
    if (p.assets.length <= 1) throw new Error("A project needs at least one asset.");
    if (assetId === p.activeAssetId) {
      const fallback = p.assets.find((a) => a.id !== assetId);
      const nextCfg = normalizeProjectConfig(fallback.config || newAssetConfig(p.project?.description));
      historyInit(nextCfg);
      setPortfolio({
        ...p,
        activeAssetId: fallback.id,
        assets: p.assets.filter((a) => a.id !== assetId).map((a) => (a.id === fallback.id ? { ...a, config: null } : a)),
      });
      return;
    }
    setPortfolio({ ...p, assets: p.assets.filter((a) => a.id !== assetId) });
  }, [historyInit]);

  const updateProjectMeta = useCallback((patch) => {
    setPortfolio((p) => ({ ...p, project: { ...p.project, ...patch } }));
  }, []);

  const value = useMemo(() => ({
    currentProject,
    isDirty,
    isSaving,
    portfolio,
    assets,
    activeAsset,
    switchAsset,
    addAsset,
    updateAsset,
    removeAsset,
    updateProjectMeta,
    newFile,
    open,
    importProjectJson,
    save,
    saveAs,
    createNewProject,
    rename,
    setIsDirty,
    setSectionDirty,
    pendingSections: pendingSectionNames,
  }), [currentProject, isDirty, isSaving, portfolio, assets, activeAsset, switchAsset, addAsset, updateAsset,
      removeAsset, updateProjectMeta, newFile, open, importProjectJson, save, saveAs, createNewProject, rename,
      setSectionDirty, pendingSectionNames]);

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
}

/** The file context when there is one (null outside <FileProvider>). */
export function useOptionalFile() {
  return useContext(FileContext);
}

export function useFile() {
  const ctx = useContext(FileContext);
  if (!ctx) throw new Error("useFile must be used inside <FileProvider>");
  return ctx;
}
